import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  WhatsappLogo, 
  Cloud, 
  HardDrives, 
  ShieldCheck, 
  Clock, 
  Headset,
  Globe,
  EnvelopeSimple,
  Lightning,
  Check,
  Cpu,
  Database,
  WifiHigh,
  Rocket,
  ArrowRight,
  ChartLineUp,
  Monitor,
  TreeStructure
} from "@phosphor-icons/react";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.6, ease: "easeOut" } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

// Panel URL (local dev vs production)
const PANEL_URL = process.env.REACT_APP_PANEL_URL || '';

// Navbar Component
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav 
      data-testid="navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass py-4' : 'py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <a href="#hero" className="flex items-center gap-3" data-testid="logo">
          <Cloud size={40} weight="duotone" className="text-purple-500" />
          <span className="font-unbounded font-bold text-2xl tracking-tight">
            Tomi<span className="gradient-text">Cloud</span>
          </span>
        </a>

        {/* Mobile: Entrar + Hamburger */}
        <div className="flex md:hidden items-center gap-3">
          <a
            href={`${PANEL_URL}/login`}
            className="text-sm text-purple-400 font-semibold px-4 py-2 border border-purple-500/40 rounded-full hover:bg-purple-500/10 transition-all active:bg-purple-500/20"
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', minHeight: '44px', display: 'inline-flex', alignItems: 'center' }}
          >
            Entrar
          </a>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            onTouchEnd={(e) => { e.preventDefault(); setMenuOpen(prev => !prev); }}
            className="text-white p-2 cursor-pointer select-none"
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            aria-label="Menu"
          >
            {menuOpen ? (
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M6 18L18 6"/></svg>
            ) : (
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
            )}
          </button>
        </div>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          <a href="#hospedagem" className="text-gray-400 hover:text-white transition-colors" data-testid="nav-hospedagem">Hospedagem</a>
          <a href="#cloud" className="text-gray-400 hover:text-white transition-colors" data-testid="nav-cloud">Cloud Servers</a>
          <a href="#monitoramento" className="text-gray-400 hover:text-white transition-colors" data-testid="nav-monitoring">Monitoramento</a>
          <a href="#email" className="text-gray-400 hover:text-white transition-colors" data-testid="nav-email">Email</a>
          <a
            href={`${PANEL_URL}/login`}
            className="text-gray-400 hover:text-white transition-colors font-medium"
          >
            Entrar
          </a>
          <a
            href={`${PANEL_URL}/register`}
            className="border border-purple-500/50 text-purple-400 hover:bg-purple-500/10 font-semibold px-5 py-2.5 rounded-full transition-all"
          >
            Cadastre-se
          </a>
          <a 
            href="https://wa.me/5527981761251?text=Olá! Gostaria de saber mais sobre os serviços da TomiCloud"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gradient text-white font-semibold px-6 py-3 rounded-full flex items-center gap-2"
            data-testid="nav-contact-btn"
          >
            <WhatsappLogo size={20} weight="fill" />
            Contato
          </a>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {menuOpen && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden glass mt-2 mx-4 rounded-2xl p-4 flex flex-col gap-3"
        >
          <a href="#hospedagem" onClick={() => setMenuOpen(false)} className="text-gray-300 hover:text-white py-3 px-4 rounded-lg hover:bg-white/5 active:bg-white/10 transition-all text-base" style={{ touchAction: "manipulation", minHeight: "44px" }}>Hospedagem</a>
          <a href="#cloud" onClick={() => setMenuOpen(false)} className="text-gray-300 hover:text-white py-3 px-4 rounded-lg hover:bg-white/5 active:bg-white/10 transition-all text-base" style={{ touchAction: "manipulation", minHeight: "44px" }}>Cloud Servers</a>
          <a href="#monitoramento" onClick={() => setMenuOpen(false)} className="text-gray-300 hover:text-white py-3 px-4 rounded-lg hover:bg-white/5 active:bg-white/10 transition-all text-base" style={{ touchAction: "manipulation", minHeight: "44px" }}>Monitoramento</a>
          <a href="#email" onClick={() => setMenuOpen(false)} className="text-gray-300 hover:text-white py-3 px-4 rounded-lg hover:bg-white/5 active:bg-white/10 transition-all text-base" style={{ touchAction: "manipulation", minHeight: "44px" }}>Email</a>
          <hr className="border-white/10" />
          <a href={`${PANEL_URL}/login`} className="text-purple-400 font-semibold py-3 px-4 rounded-lg hover:bg-purple-500/10 active:bg-purple-500/20 transition-all text-base" style={{ touchAction: "manipulation", minHeight: "48px" }}>Entrar no Painel</a>
          <a href={`${PANEL_URL}/register`} className="text-center border border-purple-500/50 text-purple-400 font-semibold py-3 px-4 rounded-full hover:bg-purple-500/10 active:bg-purple-500/20 transition-all text-base" style={{ touchAction: "manipulation", minHeight: "48px", display: "flex", alignItems: "center", justifyContent: "center" }}>Cadastre-se</a>
          <a 
            href="https://wa.me/5527981761251?text=Olá! Gostaria de saber mais sobre os serviços da TomiCloud"
            target="_blank"
            rel="noopener noreferrer"
            className="text-center btn-gradient text-white font-semibold py-2.5 px-3 rounded-full flex items-center justify-center gap-2"
          >
            <WhatsappLogo size={18} weight="fill" />
            Contato
          </a>
        </motion.div>
      )}
    </motion.nav>
  );
};

// Hero Section
// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="hero" 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden grid-pattern"
    >
      {/* Background image overlay */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: 'radial-gradient(ellipse at 30% 20%, rgba(139,92,246,0.15) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(59,130,246,0.1) 0%, transparent 50%), radial-gradient(ellipse at 50% 50%, rgba(168,85,247,0.08) 0%, transparent 70%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#05050A]/50 to-[#05050A]" />
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-sm font-medium text-purple-400">
              <Lightning size={16} weight="fill" />
              Hospedagem de Alta Performance
            </span>
          </motion.div>

          <motion.h1 
            variants={fadeInUp}
            className="font-unbounded font-black text-4xl md:text-6xl lg:text-7xl mb-6 leading-tight"
          >
            Sua presença online<br />
            <span className="gradient-text">começa aqui</span>
          </motion.h1>

          <motion.p 
            variants={fadeInUp}
            className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10"
          >
            Hospedagem de sites e servidores cloud com tecnologia de ponta, 
            suporte 24/7 e a confiabilidade que seu negócio merece.
          </motion.p>

          <motion.div 
            variants={fadeInUp}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <a 
              href="#hospedagem"
              className="btn-gradient text-white font-bold px-8 py-4 rounded-full flex items-center gap-3 text-lg"
              data-testid="hero-cta-primary"
            >
              <Rocket size={24} weight="fill" />
              Ver Planos
            </a>
            <a 
              href="https://wa.me/5527981761251?text=Olá! Gostaria de saber mais sobre os serviços da TomiCloud"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-white/20 text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-full flex items-center gap-3 transition-all"
              data-testid="hero-cta-secondary"
            >
              <WhatsappLogo size={24} weight="fill" />
              Falar com Especialista
            </a>
          </motion.div>

          {/* Features badges */}
          <motion.div 
            variants={fadeInUp}
            className="mt-16 flex flex-wrap items-center justify-center gap-6 md:gap-10"
          >
            {[
              { icon: HardDrives, text: "SSD NVMe" },
              { icon: ShieldCheck, text: "SSL Grátis" },
              { icon: Clock, text: "Backup Diário" },
              { icon: Headset, text: "Suporte 24/7" }
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-gray-400">
                <item.icon size={20} weight="duotone" className="text-purple-400" />
                <span className="text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center p-2">
          <motion.div 
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-purple-500 rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};

// Hosting Plans Section
const HostingSection = () => {
  const hostingPlans = [
    {
      name: "Hospedagem Essencial",
      description: "Ideal para sites institucionais e blogs",
      price: "49",
      features: [
        "10 GB SSD",
        "1 Domínio",
        "SSL Grátis",
        "Backup Diário",
        "Suporte 24/7",
        "Painel cPanel"
      ],
      highlighted: false,
      available: true
    },
    {
      name: "Hospedagem + Email",
      description: "Hospedagem completa com email corporativo",
      price: "89",
      features: [
        "25 GB SSD",
        "3 Domínios",
        "SSL Grátis",
        "Backup Diário",
        "Suporte 24/7",
        "10 Contas de Email",
        "Painel cPanel"
      ],
      highlighted: true,
      available: false,
      badge: "Em Breve"
    }
  ];

  return (
    <section id="hospedagem" data-testid="hosting-section" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span 
            variants={fadeInUp}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-sm font-medium text-blue-400 mb-6"
          >
            <Globe size={16} weight="fill" />
            Hospedagem de Sites
          </motion.span>
          
          <motion.h2 
            variants={fadeInUp}
            className="font-unbounded font-bold text-3xl md:text-5xl mb-4"
          >
            Planos de <span className="gradient-text">Hospedagem</span>
          </motion.h2>
          
          <motion.p 
            variants={fadeInUp}
            className="text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Hospedagem segura e rápida para seu site com todos os recursos que você precisa
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto"
        >
          {hostingPlans.map((plan, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              data-testid={`hosting-plan-${index}`}
              className={`relative rounded-2xl p-8 ${
                plan.highlighted 
                  ? 'bg-[#12121A] border-2 border-purple-500/50 shadow-[0_0_30px_rgba(109,40,217,0.15)]' 
                  : 'bg-[#12121A] border border-white/5'
              } ${plan.available ? 'card-hover' : 'opacity-70'}`}
            >
              {plan.badge && (
                <div className="absolute -top-3 right-8">
                  <span className="px-4 py-1.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-bold rounded-full pulse-badge">
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="mb-6">
                <h3 className="font-unbounded font-bold text-xl mb-2">{plan.name}</h3>
                <p className="text-gray-400 text-sm">{plan.description}</p>
              </div>

              <div className="mb-8">
                <span className="text-gray-400 text-sm">A partir de</span>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-unbounded font-black gradient-text">R${plan.price}</span>
                  <span className="text-gray-400">/mês</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-300">
                    <Check size={18} weight="bold" className="text-green-400 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {plan.available ? (
                <a
                  href="https://wa.me/5527981761251?text=Olá! Tenho interesse no plano de Hospedagem Essencial"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full btn-gradient text-white font-bold py-4 rounded-full flex items-center justify-center gap-2"
                  data-testid={`hosting-plan-${index}-btn`}
                >
                  Contratar Agora
                  <ArrowRight size={20} weight="bold" />
                </a>
              ) : (
                <button
                  disabled
                  className="w-full bg-white/5 text-gray-500 font-bold py-4 rounded-full cursor-not-allowed"
                  data-testid={`hosting-plan-${index}-btn-disabled`}
                >
                  Em Breve
                </button>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* Features highlight */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="mt-16 glass rounded-2xl p-8 md:p-12"
        >
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { icon: HardDrives, title: "HD SSD", desc: "Armazenamento ultra rápido" },
              { icon: ShieldCheck, title: "SSL Grátis", desc: "Certificado de segurança" },
              { icon: Clock, title: "Backup Diário", desc: "Todo dia à meia-noite" },
              { icon: Headset, title: "Suporte 24/7", desc: "Sempre disponível" }
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600/20 to-blue-600/20 flex items-center justify-center mb-4">
                  <item.icon size={32} weight="duotone" className="text-purple-400" />
                </div>
                <h4 className="font-unbounded font-bold mb-1">{item.title}</h4>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Cloud Servers Section
const CloudSection = () => {
  const cloudPlans = [
    {
      name: "Cloud Start",
      specs: {
        storage: "40 GB",
        ram: "4 GB",
        cpu: "Xeon",
        network: "Ilimitado"
      },
      price: "90",
      highlighted: false
    },
    {
      name: "Cloud Pro",
      specs: {
        storage: "60 GB",
        ram: "8 GB",
        cpu: "Xeon",
        network: "Ilimitado"
      },
      price: "120",
      highlighted: true,
      badge: "Mais Popular"
    },
    {
      name: "Cloud Enterprise",
      specs: {
        storage: "100 GB",
        ram: "8 GB",
        cpu: "Xeon",
        network: "Ilimitado"
      },
      price: "160",
      highlighted: false
    }
  ];

  return (
    <section id="cloud" data-testid="cloud-section" className="py-24 md:py-32 relative bg-gradient-to-b from-transparent via-purple-900/5 to-transparent">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span 
            variants={fadeInUp}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-sm font-medium text-cyan-400 mb-6"
          >
            <Cloud size={16} weight="fill" />
            Servidores Cloud
          </motion.span>
          
          <motion.h2 
            variants={fadeInUp}
            className="font-unbounded font-bold text-3xl md:text-5xl mb-4"
          >
            Servidores <span className="gradient-text">Cloud</span>
          </motion.h2>
          
          <motion.p 
            variants={fadeInUp}
            className="text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Infraestrutura robusta para suas aplicações com processadores Xeon e internet ilimitada
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-8"
        >
          {cloudPlans.map((plan, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              data-testid={`cloud-plan-${index}`}
              className={`relative rounded-2xl p-8 card-hover ${
                plan.highlighted 
                  ? 'bg-[#12121A] border-2 border-purple-500/50 shadow-[0_0_30px_rgba(109,40,217,0.15)] scale-105' 
                  : 'bg-[#12121A] border border-white/5'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-bold rounded-full whitespace-nowrap">
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="font-unbounded font-bold text-xl mb-2">{plan.name}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-unbounded font-black gradient-text">R${plan.price}</span>
                  <span className="text-gray-400">/mês</span>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4 p-3 rounded-xl bg-white/5">
                  <Database size={24} weight="duotone" className="text-purple-400" />
                  <div>
                    <p className="text-gray-400 text-xs">Armazenamento SSD</p>
                    <p className="font-bold">{plan.specs.storage}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-3 rounded-xl bg-white/5">
                  <HardDrives size={24} weight="duotone" className="text-blue-400" />
                  <div>
                    <p className="text-gray-400 text-xs">Memória RAM</p>
                    <p className="font-bold">{plan.specs.ram}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-3 rounded-xl bg-white/5">
                  <Cpu size={24} weight="duotone" className="text-cyan-400" />
                  <div>
                    <p className="text-gray-400 text-xs">Processador</p>
                    <p className="font-bold">{plan.specs.cpu}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-3 rounded-xl bg-white/5">
                  <WifiHigh size={24} weight="duotone" className="text-green-400" />
                  <div>
                    <p className="text-gray-400 text-xs">Internet</p>
                    <p className="font-bold">{plan.specs.network}</p>
                  </div>
                </div>
              </div>

              <a
                href={`https://wa.me/5527981761251?text=Olá! Tenho interesse no plano ${plan.name}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full font-bold py-4 rounded-full flex items-center justify-center gap-2 transition-all ${
                  plan.highlighted 
                    ? 'btn-gradient text-white' 
                    : 'border border-white/20 text-white hover:bg-white/10'
                }`}
                data-testid={`cloud-plan-${index}-btn`}
              >
                Contratar
                <ArrowRight size={20} weight="bold" />
              </a>
            </motion.div>
          ))}
        </motion.div>

        {/* Additional info */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="mt-12 text-center"
        >
          <p className="text-gray-400">
            Todos os planos incluem: <span className="text-white">Domínio necessário</span> • 
            <span className="text-white"> HD SSD</span> • 
            <span className="text-white"> SSL Grátis</span> • 
            <span className="text-white"> Backups Diários</span> • 
            <span className="text-white"> Suporte 24/7</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};


// Monitoring Section
const MonitoringSection = () => {
  const features = [
    {
      icon: Monitor,
      title: "Painel Centralizado",
      desc: "Gerencie todos os seus servidores Proxmox em um único dashboard com visão 360°"
    },
    {
      icon: ChartLineUp,
      title: "Métricas em Tempo Real",
      desc: "CPU, RAM, disco e rede atualizados a cada 30 segundos com gráficos visuais"
    },
    {
      icon: TreeStructure,
      title: "Gestão de VMs",
      desc: "Visualize todas as VMs de cada servidor, status, recursos e uptime em um só lugar"
    },
    {
      icon: ShieldCheck,
      title: "Backup Integrado",
      desc: "Monitore seus servidores PBS, status de backups e armazenamento disponível"
    },
  ];

  return (
    <section id="monitoramento" data-testid="monitoring-section" className="py-24 md:py-32 relative bg-gradient-to-b from-transparent via-emerald-900/5 to-transparent">
      {/* Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500 rounded-full filter blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-emerald-400 text-sm font-semibold mb-6">
            <ChartLineUp size={16} weight="fill" />
            Monitoramento Proxmox
            <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-xs font-bold rounded-full ml-1">NOVO</span>
          </motion.div>
          <motion.h2 variants={fadeInUp} className="font-unbounded text-3xl md:text-5xl font-bold mb-6">
            Centralize seus <span className="gradient-text">servidores</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-gray-400 text-lg max-w-2xl mx-auto">
            Monitore todos os seus servidores Proxmox VE e Proxmox Backup Server em um único painel profissional.
          </motion.p>
        </motion.div>

        {/* Dashboard Preview */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="mb-20"
        >
          <div className="glass rounded-2xl p-8 border border-emerald-500/10">
            {/* Mock Dashboard Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-purple-600 flex items-center justify-center">
                  <Monitor size={20} weight="fill" className="text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Dashboard de Monitoramento</h3>
                  <p className="text-gray-500 text-sm">Atualizado a cada 30s</p>
                </div>
              </div>
              <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-sm font-medium border border-emerald-500/20">
                ● Todos Online
              </span>
            </div>

            {/* Mock Server Cards */}
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { name: "SERVIDOR-001", cpu: 22, ram: 78, disk: 35, uptime: "241d 18h" },
                { name: "SERVIDOR-002", cpu: 45, ram: 62, disk: 51, uptime: "180d 5h" },
                { name: "PBS-BACKUP", cpu: 8, ram: 31, disk: 67, uptime: "241d 18h", isPbs: true },
              ].map((server, i) => (
                <div key={i} className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-5 hover:border-emerald-500/20 transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-white font-medium text-sm">{server.name}</span>
                    </div>
                    <span className="text-gray-500 text-xs">{server.uptime}</span>
                  </div>
                  {/* Resource Bars */}
                  {[
                    { label: "CPU", value: server.cpu, color: "from-amber-500 to-orange-500" },
                    { label: "RAM", value: server.ram, color: "from-purple-500 to-violet-500" },
                    { label: server.isPbs ? "Storage" : "Disco", value: server.disk, color: "from-blue-500 to-cyan-500" },
                  ].map((res, j) => (
                    <div key={j} className="mb-2.5">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-400">{res.label}</span>
                        <span className="text-gray-300 font-medium">{res.value}%</span>
                      </div>
                      <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                        <div
                          className={`h-full bg-gradient-to-r ${res.color} rounded-full transition-all duration-1000`}
                          style={{ width: `${res.value}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              data-testid={`monitoring-feature-${index}`}
              className="glass rounded-2xl p-6 hover:border-emerald-500/20 transition-all group"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-purple-600/20 border border-emerald-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <feature.icon size={24} weight="duotone" className="text-emerald-400" />
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">{feature.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{feature.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="text-center"
        >
          <a
            href="https://wa.me/5527981761251?text=Olá! Gostaria de saber mais sobre o serviço de Monitoramento Proxmox"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gradient text-white font-semibold px-8 py-4 rounded-full inline-flex items-center gap-3 text-lg"
            data-testid="monitoring-cta-btn"
          >
            <WhatsappLogo size={24} weight="fill" />
            Quero Monitoramento
            <ArrowRight size={20} />
          </a>
          <p className="text-gray-500 text-sm mt-4">Fale com um especialista e configure em minutos</p>
        </motion.div>
      </div>
    </section>
  );
};

// Email Section (Coming Soon)
const EmailSection = () => {
  return (
    <section id="email" data-testid="email-section" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'radial-gradient(ellipse at 40% 30%, rgba(59,130,246,0.12) 0%, transparent 60%), radial-gradient(ellipse at 60% 70%, rgba(139,92,246,0.1) 0%, transparent 60%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="glass rounded-3xl p-12 md:p-20 text-center relative overflow-hidden"
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600/10 rounded-full blur-[100px]" />

          <motion.div variants={fadeInUp} className="relative z-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-600/30 to-blue-600/30 mb-8">
              <EnvelopeSimple size={40} weight="duotone" className="text-purple-400" />
            </div>

            <span className="inline-block px-4 py-1.5 bg-yellow-500/20 text-yellow-400 text-sm font-bold rounded-full mb-6 pulse-badge">
              Em Desenvolvimento
            </span>

            <h2 className="font-unbounded font-bold text-3xl md:text-5xl mb-6">
              Email <span className="gradient-text">Corporativo</span>
            </h2>

            <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
              Em breve você terá acesso ao nosso serviço de email corporativo profissional. 
              Comunicação segura e confiável com o domínio da sua empresa.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-6 mb-10">
              {[
                "Domínio Personalizado",
                "Anti-spam Avançado",
                "Webmail Responsivo",
                "Suporte Técnico"
              ].map((feature, index) => (
                <span key={index} className="flex items-center gap-2 text-gray-400">
                  <Check size={18} weight="bold" className="text-purple-400" />
                  {feature}
                </span>
              ))}
            </div>

            <a
              href="https://wa.me/5527981761251?text=Olá! Gostaria de ser notificado quando o Email Corporativo estiver disponível"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 border border-white/20 text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-full transition-all"
              data-testid="email-notify-btn"
            >
              <WhatsappLogo size={24} weight="fill" />
              Avise-me quando lançar
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section data-testid="cta-section" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-unbounded font-bold text-3xl md:text-5xl mb-6"
          >
            Pronto para <span className="gradient-text">começar</span>?
          </motion.h2>
          
          <motion.p 
            variants={fadeInUp}
            className="text-gray-400 text-lg max-w-2xl mx-auto mb-10"
          >
            Entre em contato e leve sua presença online para o próximo nível
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://wa.me/5527981761251?text=Olá! Gostaria de contratar um serviço da TomiCloud"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gradient text-white font-bold px-10 py-5 rounded-full flex items-center gap-3 text-lg"
              data-testid="cta-whatsapp-btn"
            >
              <WhatsappLogo size={28} weight="fill" />
              Falar no WhatsApp
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-12 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Cloud size={32} weight="duotone" className="text-purple-500" />
            <span className="font-unbounded font-bold text-xl">
              Tomi<span className="gradient-text">Cloud</span>
            </span>
          </div>

          <div className="flex items-center gap-6 text-sm text-gray-400">
            <a href="#hospedagem" className="hover:text-white transition-colors">Hospedagem</a>
            <a href="#cloud" className="hover:text-white transition-colors">Cloud Servers</a>
            <a href="#monitoramento" className="hover:text-white transition-colors">Monitoramento</a>
            <a href="#email" className="hover:text-white transition-colors">Email</a>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Desenvolvido por</span>
            <a 
              href="https://tomich.com.br" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-purple-400 hover:text-purple-300 transition-colors font-semibold"
            >
              Tomich Tecnologia
            </a>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-white/5 text-center text-sm text-gray-500">
          <p>© {new Date().getFullYear()} TomiCloud. Todos os direitos reservados.</p>
        </div>
      </div>
    </footer>
  );
};

// Scroll to Top Button
const ScrollToTop = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShow(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!show) return null;

  return (
    <motion.button
      onClick={scrollToTop}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      className="fixed bottom-8 left-8 z-50 w-14 h-14 bg-purple-600/80 hover:bg-purple-600 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg shadow-purple-600/20 transition-colors cursor-pointer"
      aria-label="Voltar ao topo"
    >
      <ArrowRight size={24} weight="bold" className="text-white -rotate-90" />
    </motion.button>
  );
};

// Floating WhatsApp Button
const FloatingWhatsApp = () => {
  return (
    <motion.a
      href="https://wa.me/5527981761251?text=Olá! Gostaria de saber mais sobre os serviços da TomiCloud"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="floating-whatsapp"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 1, type: "spring" }}
      className="fixed bottom-8 right-8 z-50 w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30 hover:bg-green-600 transition-colors float-animation"
    >
      <WhatsappLogo size={32} weight="fill" className="text-white" />
    </motion.a>
  );
};

// Main App Component
function App() {
  return (
    <div className="min-h-screen">
      <Navbar />
      <HeroSection />
      <HostingSection />
      <CloudSection />
      <MonitoringSection />
      <EmailSection />
      <CTASection />
      <Footer />
      <FloatingWhatsApp />
      <ScrollToTop />
    </div>
  );
}

export default App;
